/**
 * Bundle Utilities Module
 * Centralized utilities for managing product bundles in the cart
 *
 * @module BundleUtilities
 */

(function() {
  'use strict';

  // Configuration constants
  const BUNDLE_CONFIG = {
    MAX_RETRIES: 2,
    POLL_INTERVAL: 200,
    MAX_POLLS: 25,
    MIN_DISCOUNT_WAIT_POLLS: 10,
    BUNDLE_ID_PREFIX: '_canon_bundle_id',
    BUNDLE_ITEMS_EXPECTED: 2
  };

  /**
   * Generate a deterministic bundle ID from variant IDs
   * @param {string|number} primaryVariantId - Primary product variant ID
   * @param {string|number} bundleVariantId - Bundle product variant ID
   * @returns {string} Unique bundle identifier
   */
  function generateBundleId(primaryVariantId, bundleVariantId) {
    return [primaryVariantId, bundleVariantId]
      .map(id => String(id))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join('_');
  }

  /**
   * Check if a bundle exists in the cart
   * @param {string} bundleId - Bundle identifier to check
   * @returns {Promise<boolean>} Promise resolving to existence status
   */
  function checkBundleExists(bundleId) {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        return cart.items.some(item =>
          item.properties &&
          item.properties[BUNDLE_CONFIG.BUNDLE_ID_PREFIX] === bundleId
        );
      })
      .catch(error => {
        // Silently handle error
        return false;
      });
  }

  /**
   * Get all bundle items from cart
   * @param {string} [bundleId] - Optional specific bundle ID to filter by
   * @returns {Promise<Array>} Promise resolving to array of bundle items
   */
  function getBundleItems(bundleId) {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        return cart.items.filter(item => {
          const hasBundle = item.properties && item.properties[BUNDLE_CONFIG.BUNDLE_ID_PREFIX];
          if (!bundleId) return hasBundle;
          return hasBundle && item.properties[BUNDLE_CONFIG.BUNDLE_ID_PREFIX] === bundleId;
        });
      })
      .catch(error => {
        // Silently handle error
        return [];
      });
  }

  /**
   * Remove a bundle from the cart
   * @param {string} bundleId - Bundle identifier to remove
   * @param {HTMLElement} [containerEl] - Optional container element for loading states
   * @param {string} [itemSelector] - Optional item selector for loading states
   * @param {Function} [refreshCallback] - Optional callback after removal
   * @returns {Promise} Promise resolving when bundle is removed
   */
  function removeBundleFromCart(bundleId, containerEl, itemSelector, refreshCallback) {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Find all items with matching bundle ID
        const bundleItems = cart.items.filter(item =>
          item.properties &&
          item.properties[BUNDLE_CONFIG.BUNDLE_ID_PREFIX] === bundleId
        );

        if (bundleItems.length === 0) return Promise.resolve();

        // Apply loading states if container provided
        if (containerEl && itemSelector) {
          applyLoadingStates(bundleItems, cart.items, containerEl, itemSelector);
        }

        // Build updates object for atomic removal
        const updates = {};
        bundleItems.forEach(item => {
          updates[item.key] = 0;
        });

        // Remove all bundle items atomically
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ updates })
        })
        .then(response => response.json())
        .then(() => {
          if (refreshCallback) refreshCallback();
        });
      });
  }

  /**
   * Update quantity for all items in a bundle
   * @param {string} bundleId - Bundle identifier
   * @param {number} newQuantity - New quantity to set
   * @param {Array<string>} [sections] - Cart sections to update
   * @returns {Promise} Promise resolving to updated cart state
   */
  function updateBundleQuantity(bundleId, newQuantity, sections = []) {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const bundleItems = cart.items.filter(item =>
          item.properties &&
          item.properties[BUNDLE_CONFIG.BUNDLE_ID_PREFIX] === bundleId
        );

        if (bundleItems.length === 0) {
          throw new Error('Bundle not found in cart');
        }

        // Build updates for all bundle items
        const updates = {};
        bundleItems.forEach(item => {
          updates[item.key] = parseInt(newQuantity);
        });

        // Update atomically
        const requestBody = { updates };
        if (sections.length > 0) {
          requestBody.sections = sections;
        }

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }).then(response => response.json());
      });
  }

  /**
   * Apply loading states to bundle items in UI
   * @private
   */
  function applyLoadingStates(bundleItems, allItems, containerEl, itemSelector) {
    bundleItems.forEach(item => {
      const lineNumber = allItems.indexOf(item) + 1;
      const itemElement = containerEl.querySelector(`${itemSelector}${lineNumber}`);
      if (itemElement) {
        itemElement.classList.add('loading', 'thb-loading');
      }
    });
  }

  /**
   * Check if Free Bundle Promotion discount is applied
   * @param {Object} cart - Cart object
   * @param {Array} bundleItems - Bundle items to check
   * @returns {boolean} Whether discount is detected
   */
  function checkForFBPDiscount(cart, bundleItems) {
    // Check for line item discounts
    const hasLineDiscount = bundleItems.some(item =>
      item.line_level_discount_allocations &&
      item.line_level_discount_allocations.length > 0
    );

    // Check for cart level discounts
    const hasCartDiscount = cart.cart_level_discount_applications &&
                           cart.cart_level_discount_applications.length > 0;

    return hasLineDiscount || hasCartDiscount;
  }

  // Export to window for global access
  window.BundleUtils = {
    config: BUNDLE_CONFIG,
    generateBundleId,
    checkBundleExists,
    getBundleItems,
    removeBundleFromCart,
    updateBundleQuantity,
    checkForFBPDiscount
  };

  // Maintain backward compatibility
  if (typeof window.removeBundleFromCart === 'undefined') {
    window.removeBundleFromCart = removeBundleFromCart;
  }
})();